import axios, { AxiosRequestConfig } from 'axios';

function isHtmlPayload(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith('<!doctype html') || normalized.startsWith('<html');
}

function toFriendlyUpstreamError(status?: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return 'The Sentinel service is temporarily unavailable. Please retry in a moment.';
  }

  return 'The Sentinel service returned an unexpected response. Please retry in a moment.';
}

function normalizeErrorPayload(error: any): unknown {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (typeof data === 'string') {
    if (isHtmlPayload(data)) {
      return toFriendlyUpstreamError(status);
    }
    return data;
  }

  if (data && typeof data === 'object') {
    const message = typeof data.message === 'string' ? data.message : null;
    const errorText = typeof data.error === 'string' ? data.error : null;
    if ((message && isHtmlPayload(message)) || (errorText && isHtmlPayload(errorText))) {
      return { ...data, message: toFriendlyUpstreamError(status), error: toFriendlyUpstreamError(status) };
    }
    return data;
  }

  if (status) {
    return toFriendlyUpstreamError(status);
  }

  return null;
}

function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_APP_API_URL || 'http://localhost:3010/';

  if (typeof window === 'undefined') {
    return configured;
  }

  try {
    const url = new URL(configured, window.location.origin);
    const configuredHost = url.hostname.trim().toLowerCase();
    const currentHost = window.location.hostname.trim().toLowerCase();
    const configuredIsLoopback = configuredHost === '127.0.0.1' || configuredHost === 'localhost';
    const currentIsLoopback = currentHost === '127.0.0.1' || currentHost === 'localhost';

    // When the dev app is opened from another machine on the LAN, a loopback API URL
    // incorrectly points the browser at its own localhost. Rebind that host to the
    // page host while preserving the configured API port/path.
    if (configuredIsLoopback && !currentIsLoopback) {
      url.hostname = window.location.hostname;
    }

    return url.toString();
  } catch {
    return configured;
  }
}

export const apiBaseUrl = resolveApiBaseUrl();

const axiosServices = axios.create({ baseURL: apiBaseUrl });

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('serviceToken');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && !window.location.href.includes('/login')) {
      localStorage.removeItem('serviceToken');
      redirectWithBasePath('/login');
    }

    const normalizedPayload = normalizeErrorPayload(error);
    if (normalizedPayload) {
      return Promise.reject(normalizedPayload);
    }

    if (error?.message) {
      return Promise.reject(error.message);
    }

    return Promise.reject('Wrong Services');
  }
);

export default axiosServices;

export function buildAuthenticatedEventStreamUrl(path: string | null): string | null {
  if (!path) {
    return null;
  }

  const url = new URL(path, apiBaseUrl);
  const accessToken = localStorage.getItem('serviceToken');
  if (accessToken) {
    url.searchParams.set('access_token', accessToken);
  }

  return url.toString();
}

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
};

export const fetcherPost = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.post(url, { ...config });

  return res.data;
};

export function redirectWithBasePath(path: string) {
  const basePath = import.meta.env.DEV ? '' : import.meta.env.VITE_APP_BASE_NAME || process.env.VITE_APP_BASE_NAME || ''; // adjust for Vite, CRA, etc.
  window.location.pathname = `${basePath.replace(/\/$/, '')}${path}`;
}
